'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'

import { TTermsForm, ZTermsForm } from '@/features/sign-up'
import { Button, Form, Separator, TextButton } from '@/shared/ui'

type Props = {
  defaultValues?: TTermsForm
  onFormFieldChange: (formValues: TTermsForm) => void
  onSubmit: (formValues: TTermsForm) => void
}

export const TermsForm = ({
  onSubmit,
  defaultValues,
  onFormFieldChange,
}: Props) => {
  const t = useTranslations('signup_terms')

  const form = useForm<TTermsForm>({
    resolver: zodResolver(ZTermsForm),
    defaultValues,
  })

  const watch = useWatch({
    control: form.control,
  })

  const { termsOfService, privacyConsent, marketingAgree, ageOver19 } = watch

  const isRequiredAllChecked = termsOfService && privacyConsent && ageOver19
  const isAllChecked = isRequiredAllChecked && marketingAgree

  const handleSelectAll = (checked: boolean) => {
    form.setValue('termsOfService', checked)
    form.setValue('privacyConsent', checked)
    form.setValue('marketingAgree', checked)
    form.setValue('ageOver19', checked)
  }

  useEffect(() => {
    onFormFieldChange(watch as TTermsForm)
  }, [watch, onFormFieldChange])

  return (
    <Form.Root form={form} className="space-y-6">
      <Form.Field name="">
        <Form.Checkbox
          weight="bold"
          checked={isAllChecked}
          onCheckedChange={handleSelectAll}
        >
          {t('agree_all')}
        </Form.Checkbox>
      </Form.Field>

      <Separator />

      <div className="flex items-center justify-between">
        <Form.Field name="termsOfService">
          <Form.Checkbox size="small" weight="medium">
            {t('service_terms_required')}
          </Form.Checkbox>
        </Form.Field>
        <TextButton underline color="gray" asChild>
          <a href="/terms/using" target="_blank" rel="noopener noreferrer">
            {t('button.view')}
          </a>
        </TextButton>
      </div>
      <div className="flex items-center justify-between">
        <Form.Field name="privacyConsent">
          <Form.Checkbox size="small" weight="medium">
            {t('privacy_collection_required')}
          </Form.Checkbox>
        </Form.Field>
        <TextButton underline color="gray" asChild>
          <a
            href="/terms/privacy-consent"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('button.view')}
          </a>
        </TextButton>
      </div>
      <div className="flex items-center justify-between">
        <Form.Field name="marketingAgree">
          <Form.Checkbox size="small" weight="medium">
            {t('marketing_opt_in')}
          </Form.Checkbox>
        </Form.Field>
        <TextButton underline color="gray" asChild>
          <a href="/terms/marketing" target="_blank" rel="noopener noreferrer">
            {t('button.view')}
          </a>
        </TextButton>
      </div>
      <Form.Field name="ageOver19">
        <Form.Checkbox size="small" weight="medium">
          {t('age_over_19_required')}
        </Form.Checkbox>
      </Form.Field>

      <Button
        size="large"
        className="mt-9"
        type="submit"
        fullWidth
        onClick={form.handleSubmit(onSubmit)}
        disabled={!isRequiredAllChecked}
      >
        {t('button.next')}
      </Button>
    </Form.Root>
  )
}
